import { DESTINATION_CATALOG, DESTINATION_TYPES } from "./destination-catalog";
// Readable delivery outcomes for the Activity feed (review S5).
//
// The destination workers store a machine string in
// event_deliveries.errorMessage (the shared processor and the adapters in
// src/destinations/), and the feed used to print it raw: "skipped:
// tier-gate: blocked:expired". Every skip string gets a sentence here;
// delivery-note.test.ts scans those source files, so a new skip string
// can't ship without one. Skipped deliveries are never re-sent, so no
// sentence may promise they will be.
//
// A replay of a dead letter (review S7) writes its success row with
// errorMessage "replay" and its skips with a "replay:" prefix.

export type NoteTone = "success" | "critical" | "warning" | "info" | undefined;

const TIER_GATE = "tier-gate:";

// "tier-gate: blocked:<reason>", reason from src/lib/billing-entitlement.ts.
const TIER_REASONS: Record<string, string> = {
  free_period_ended: "Not sent: your 30 free days ended. Pick a plan to resume.",
  trial_expired: "Not sent: your trial ended. Pick a plan to resume.",
  cancelled_expired: "Not sent: your subscription ended. Pick a plan to resume.",
  expired: "Not sent: your subscription expired. Pick a plan to resume.",
  declined: "Not sent: the charge was declined in Shopify. Pick a plan to resume.",
  frozen: "Not sent: Shopify froze your subscription. Update your billing in Shopify to resume.",
};
const TIER_FALLBACK = "Not sent: no active plan. Open billing to resume.";

// "no <platform> mapping for event_name=<name>" (meta-capi, google-ads,
// pinterest, tiktok).
// skipToken (lowercased) -> the sentence name. Built from the catalog so the
// token an adapter actually emits and the name shown here cannot disagree:
// when they do, the merchant is shown the raw token.
const PLATFORM_NAMES: Record<string, string> = Object.fromEntries(
  DESTINATION_TYPES.map((t) => [
    DESTINATION_CATALOG[t].skipToken.toLowerCase(),
    DESTINATION_CATALOG[t].name,
  ]),
);

// "replay: too old for <destination type> (limit <hours>h)", from the
// processor's replay age gate (the adapters' replay.maxAgeMs).
const REPLAY_TOO_OLD = /^replay: too old for (\S+) \(limit (\d+)h\)$/;
/** Short platform names by destination type, for sentences. */
export const DESTINATION_NAMES: Record<string, string> = Object.fromEntries(
  DESTINATION_TYPES.map((t) => [t, DESTINATION_CATALOG[t].name]),
);

function replayTooOld(errorMessage: string): string {
  const m = REPLAY_TOO_OLD.exec(errorMessage);
  if (!m) return "Not re-sent: too old to re-send to this destination.";
  const hours = Number(m[2]);
  const limit = hours >= 48 && hours % 24 === 0 ? `${hours / 24}-day` : `${hours}-hour`;
  return `Not re-sent: older than the ${limit} re-send limit for ${DESTINATION_NAMES[m[1]!] ?? "this destination"}.`;
}

/** A plan-gate skip: shown in warning tone, because the merchant can fix it. */
export function isTierGateSkip(status: string, errorMessage: string | null): boolean {
  return status === "skipped" && (errorMessage ?? "").startsWith(TIER_GATE);
}

/** The sentence for a skip string, or null when it isn't one the workers write. */
export function skipReason(errorMessage: string): string | null {
  if (errorMessage.startsWith(TIER_GATE)) {
    const reason = errorMessage.slice(TIER_GATE.length).trim().replace(/^blocked:/, "");
    return TIER_REASONS[reason] ?? TIER_FALLBACK;
  }
  if (errorMessage === "destination disabled") {
    return "Not sent: this destination was switched off.";
  }
  if (errorMessage === "marketing consent denied") {
    return "Not sent: the shopper declined marketing tracking.";
  }
  if (errorMessage.startsWith("replay: too old")) return replayTooOld(errorMessage);
  if (errorMessage === "replay: already delivered") {
    return "Not re-sent: the destination already has this event.";
  }
  if (errorMessage.startsWith("already delivered")) {
    return "Not sent again: GA4 already has this event and can't drop a second copy.";
  }
  if (errorMessage.startsWith("no klaviyo mapping or no identity")) {
    return "Not sent: Klaviyo doesn't take this event, or the shopper had no email, phone or customer ID to match.";
  }
  // Reddit's schema marks no user field required and it answers 200 for an
  // event with nobody attached, so nothing on the wire enforces this floor
  // (its CAPI requirements page does call match keys required, which its own
  // match-key form contradicts). We enforce it regardless: an event with no
  // identifier cannot attribute, and sending it spends quota to say nothing.
  if (errorMessage.startsWith("no reddit mapping or no identifiers")) {
    return "Not sent: Reddit doesn't take this event, or the shopper had no email, phone, Reddit click ID or customer ID to match.";
  }
  // Attentive matches a subscriber profile on the raw email or phone, and its
  // event user object has nowhere to put anything else: no IP, no user agent,
  // no anonymous id. So an event with neither identifier has nobody to attach
  // to. The item clause is real too, because every Attentive endpoint requires
  // at least one item with a price and a currency.
  if (errorMessage.startsWith("no attentive mapping or no identifiers")) {
    return "Not sent: Attentive doesn't take this event, or it had nothing Attentive needs: an email or phone to match a subscriber on, and at least one item with a price in a currency we recognise.";
  }
  // Snapchat's floor is an email, or a phone that states its country code, or
  // BOTH an IP and a browser user agent. A click ID or a cookie alone does not
  // satisfy it, which is why this sentence names what does.
  if (errorMessage.startsWith("no snapchat mapping or no identifiers")) {
    return "Not sent: Snapchat doesn't take this event, or the shopper had no email, phone, or IP address and browser for Snapchat to match.";
  }
  // Microsoft refuses an event with nobody attached to it: the API requires at
  // least one of a click id, a hashed email, a hashed phone or a customer id,
  // and a raw IP and user-agent explicitly do not count. So this skip covers
  // both reasons, the way Klaviyo's does.
  if (errorMessage.startsWith("no microsoft-ads mapping or no identifier")) {
    return "Not sent: Microsoft Ads doesn't take this event, or the shopper had no Microsoft click ID, email, phone or customer ID to match.";
  }
  if (errorMessage.startsWith("no GA4 mapping or missing client_id")) {
    return "Not sent: GA4 doesn't take this event, or the browser's GA ID (the _ga cookie) was missing.";
  }
  const unmapped = /^no (\S+) mapping for event_name=/i.exec(errorMessage);
  if (unmapped) {
    const platform = PLATFORM_NAMES[unmapped[1]!.toLowerCase()] ?? unmapped[1]!;
    return `Not sent: ${platform} doesn't take this event type.`;
  }
  return null;
}

/** The sentence and badge tone for one delivery row. */
export function deliveryNote(status: string, errorMessage: string | null): { text: string; tone: NoteTone } {
  const raw = errorMessage ?? "";
  switch (status) {
    case "success":
      return { text: raw === "replay" ? "Sent on retry" : "Sent", tone: "success" };
    case "dry_run":
      return { text: "Not sent: test mode (dry run) is on for this destination.", tone: "info" };
    case "skipped":
      // By-design skips (unmapped event, consent, destination off) are
      // expected, so neutral; a plan-gate skip is amber because the
      // merchant can fix it.
      return {
        text: skipReason(raw) ?? (raw ? `Not sent: ${raw}` : "Not sent"),
        tone: isTierGateSkip(status, errorMessage) ? "warning" : undefined,
      };
    case "failure":
      return { text: failureText(raw), tone: "critical" };
    default:
      return { text: raw ? `${status}: ${raw}` : status, tone: undefined };
  }
}

/** One delivery row as the Activity feed receives it. */
export interface DeliveryRow {
  destinationType: string;
  status: string;
  errorMessage: string | null;
  attemptedAt: string;
}

// Which row stands for a destination's outcome: any success, else any
// failure, else the latest row.
function outcomeRank(status: string): number {
  if (status === "success") return 2;
  if (status === "failure") return 1;
  return 0;
}

/**
 * One row per destination: the one its badge shows (critic-1 P15). An event
 * can hold several rows for one destination: a failure per retried attempt
 * and then a success, or a failure and, days later, its replay. A success
 * wins, since the event got there; else a failure wins over a later skip,
 * since a replay skipped for age or plan doesn't undo the failure (the rule
 * the dashboard's delivery counts use, src/lib/delivery-metrics.ts); among
 * rows of the same standing the latest attempt wins, a tie going to the
 * later row. Destinations keep the order they first appear in.
 */
export function finalOutcomes<T extends DeliveryRow>(rows: readonly T[]): T[] {
  const picked = new Map<string, T>();
  for (const row of rows) {
    const current = picked.get(row.destinationType);
    if (!current) {
      picked.set(row.destinationType, row);
      continue;
    }
    const byRank = outcomeRank(row.status) - outcomeRank(current.status);
    const earlier = Date.parse(row.attemptedAt) < Date.parse(current.attemptedAt);
    if (byRank > 0 || (byRank === 0 && !earlier)) picked.set(row.destinationType, row);
  }
  return [...picked.values()];
}

function failureText(raw: string): string {
  if (raw === "invalid destination credentials") {
    return "Failed: the saved credentials can't be read. Reconnect this destination.";
  }
  if (raw.startsWith("auth: ")) {
    return "Failed: Google Ads access expired or was revoked. Reconnect Google Ads.";
  }
  const attempt = /^attempt (\d+)\/(\d+): (.*)$/s.exec(raw);
  if (attempt) return `Failed on attempt ${attempt[1]} of ${attempt[2]}: ${attempt[3]}`;
  return raw ? `Failed: ${raw}` : "Failed";
}
